/**
 * CR298: Card Faces Data Model
 * 
 * Extends card layout config to support multiple faces:
 * - front: The default card face (existing layout)
 * - back: Flip back face (for DealDetailsFlip)
 * - drawer: Expanded side drawer content (for DealDetailsPanel)
 * 
 * Each face uses the existing section/block structure.
 */

import type { ExtendedSectionToggles } from "@/components/admin/command-center/SectionTogglesPanel";
import type { CardStyling } from "@/components/admin/command-center/CardStylingPanel";
import type { HeaderComposition } from "@/components/admin/command-center/HeaderCompositionPanel";
import type { CardTemplateLabels } from "@/providers/DealCardConfigProvider";

// ================================================================
// FACE TYPES
// ================================================================

export type CardFaceType = "front" | "back" | "drawer";

export type DrawerVariant = "SIDE" | "SLIDE_UP" | "MODAL";

/**
 * Configuration for a single card face.
 * Each face can inherit from another or have its own config.
 */
export interface CardFaceConfig {
  /** Whether this face inherits from front */
  inheritFromFront: boolean;
  
  /** Section toggles (if not inheriting) */
  sections?: Partial<ExtendedSectionToggles>;
  
  /** Section order (if not inheriting) */
  sectionOrder?: string[];
  
  /** Labels (if not inheriting) */
  labels?: CardTemplateLabels;
  
  /** Styling (if not inheriting) */
  styling?: Partial<CardStyling>;
  
  /** Header composition (if not inheriting) */
  header?: Partial<HeaderComposition>;
}

/**
 * Complete faces configuration for a card template.
 */
export interface CardFacesConfig {
  /** Front face (always uses base template config) */
  front: "base";
  
  /** Back face config */
  back: CardFaceConfig;
  
  /** Drawer face config */
  drawer: CardFaceConfig;
  
  /** Drawer display variant (default: SIDE) */
  drawerVariant: DrawerVariant;
}

// ================================================================
// DEFAULTS
// ================================================================

export const DEFAULT_FACE_CONFIG: CardFaceConfig = {
  inheritFromFront: true,
};

export const DEFAULT_FACES_CONFIG: CardFacesConfig = {
  front: "base",
  back: {
    inheritFromFront: true,
  },
  drawer: {
    inheritFromFront: true,
  },
  drawerVariant: "SIDE",
};

// ================================================================
// PREVIEW VIEWPORT TYPES
// ================================================================

export type PreviewViewportWidth = 200 | 400 | 600 | "custom";

/** Height presets matching Layout Studio breakpoints (200 / 400 / 600) */
export type PreviewViewportHeight = "auto" | 200 | 400 | 600 | "custom";

export type PreviewScaleMode = "fit" | "actual" | "custom";

/** CR311: Display mode for preview container */
export type PreviewDisplayMode = "fit" | "full-card" | "fullscreen";

/** CR308: Height mode for preview container */
export type PreviewHeightMode = "auto" | "scroll" | "fixed";

/** CR311: Face view mode */
export type PreviewFaceView = "front" | "back" | "both";

/** CR309: Renderer version for preview */
export type PreviewRendererVersion = "v1" | "v2";

export interface PreviewDebugOverlays {
  showFoldLine: boolean;
  showSectionBounds: boolean;
  showHiddenBlocks: boolean;
  showSafePadding: boolean;
}

export interface PreviewOverflowMode {
  scrollInside: boolean;
  expandContainer: boolean;
}

export interface PreviewControlsState {
  viewportWidth: PreviewViewportWidth;
  customWidth: number;
  viewportHeight: PreviewViewportHeight;
  customHeight: number;
  lockRatio: boolean;
  scaleMode: PreviewScaleMode;
  customScale: number;
  overlays: PreviewDebugOverlays;
  overflow: PreviewOverflowMode;
  selectedFace: CardFaceType;
  /** CR311: Display mode (fit/full-card/fullscreen) */
  displayMode: PreviewDisplayMode;
  /** CR311: Face view (front/back/both) */
  faceView: PreviewFaceView;
  /** CR311: Whether advanced controls are visible */
  showAdvanced: boolean;
  /** CR309: Renderer version (v1 or v2) */
  rendererVersion: PreviewRendererVersion;
  /** CR308: Height mode (auto/scroll/fixed) */
  heightMode: PreviewHeightMode;
}

export const DEFAULT_PREVIEW_CONTROLS: PreviewControlsState = {
  viewportWidth: 400,
  customWidth: 400,
  viewportHeight: "auto",
  customHeight: 520,
  lockRatio: false,
  scaleMode: "fit",
  customScale: 100,
  overlays: {
    showFoldLine: false,
    showSectionBounds: false,
    showHiddenBlocks: false,
    showSafePadding: false,
  },
  overflow: {
    scrollInside: true,
    expandContainer: false,
  },
  selectedFace: "front",
  displayMode: "fit",
  faceView: "front",
  showAdvanced: false,
  rendererVersion: "v1",
  heightMode: "auto",
};

// LocalStorage key for persisting preview controls
export const PREVIEW_CONTROLS_STORAGE_KEY = "preview-workbench-controls-v1";

// ================================================================
// HELPERS
// ================================================================

/**
 * Get the effective width in pixels for a viewport setting.
 */
export function getEffectiveWidth(
  viewportWidth: PreviewViewportWidth,
  customWidth: number
): number {
  if (viewportWidth === "custom") {
    return Math.max(200, Math.min(1000, customWidth));
  }
  return viewportWidth;
}

/**
 * Get the effective height in pixels for a viewport setting.
 * Returns null for "auto" mode (content-driven height).
 */
export function getEffectiveHeight(
  viewportHeight: PreviewViewportHeight,
  customHeight: number
): number | null {
  if (viewportHeight === "auto") {
    return null;
  }
  if (viewportHeight === "custom") {
    return Math.max(200, Math.min(1200, customHeight));
  }
  return viewportHeight;
}

/**
 * Get the scale factor for a scale mode.
 * When height is fixed, fits both dimensions.
 */
export function getScaleFactor(
  scaleMode: PreviewScaleMode,
  customScale: number,
  containerWidth: number,
  contentWidth: number,
  containerHeight?: number,
  contentHeight?: number | null
): number {
  switch (scaleMode) {
    case "fit": {
      const widthScale = containerWidth / contentWidth;
      if (contentHeight && containerHeight) {
        const heightScale = containerHeight / contentHeight;
        return Math.min(1, widthScale, heightScale);
      }
      return Math.min(1, widthScale);
    }
    case "actual":
      return 1;
    case "custom":
      return Math.max(0.5, Math.min(1.5, customScale / 100));
    default:
      return 1;
  }
}

/**
 * Load preview controls from localStorage.
 */
export function loadPreviewControls(): PreviewControlsState {
  if (typeof window === "undefined") return DEFAULT_PREVIEW_CONTROLS;
  
  try {
    const stored = localStorage.getItem(PREVIEW_CONTROLS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle any missing fields from older versions
      return { ...DEFAULT_PREVIEW_CONTROLS, ...parsed };
    }
  } catch (e) {
    console.warn("[PreviewControls] Failed to load from localStorage:", e);
  }
  return DEFAULT_PREVIEW_CONTROLS;
}

/**
 * Save preview controls to localStorage.
 */
export function savePreviewControls(controls: PreviewControlsState): void {
  if (typeof window === "undefined") return;
  
  try {
    localStorage.setItem(PREVIEW_CONTROLS_STORAGE_KEY, JSON.stringify(controls));
  } catch (e) {
    console.warn("[PreviewControls] Failed to save to localStorage:", e);
  }
}
